import { styled } from "goober";
import { type DragEvent, type ReactNode, useState } from "react";
import { Box } from "../../Box";
import { Input } from "../../Input";
import { Text } from "../../Text";
import type { SsoConfig } from "./constants";
import { Disclosure } from "./Disclosure";
import { ModeTabs } from "./ModeTabs";
import { CertTextarea, Field, MonoTextarea, SubLabel } from "./shared";

type Method = "metadata" | "manual";
type MetaSource = "url" | "paste";

const META_SOURCES: { value: MetaSource; label: string }[] = [
  { value: "url", label: "Metadata URL" },
  { value: "paste", label: "Paste XML" },
];

const DropZone = styled("div")`
  position: relative;
  border-radius: 8px;
  &[data-dragging="true"] textarea {
    outline: 0.5px solid var(--control-bg);
    box-shadow: 0 0 0 3px var(--border-alpha);
  }
`;

function ManualField({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <Field>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md">{label}</Text>
      </Box>
      {children}
      <Box horizontal={8} top={6}>
        <Text variant="text-small" color="var(--text-secondary)">
          {hint}
        </Text>
      </Box>
      {error && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-error)">
            {error}
          </Text>
        </Box>
      )}
    </Field>
  );
}

function looksLikePemCert(text: string): boolean {
  const body = text
    .replace(/-----(BEGIN|END) CERTIFICATE-----/g, "")
    .replace(/\s/g, "");
  return body.length > 0 && /^[A-Za-z0-9+/=]+$/.test(body);
}

export function MetadataImport({
  config,
  onConfigChange,
  existing,
}: {
  config: SsoConfig;
  onConfigChange: (updater: (prev: SsoConfig) => SsoConfig) => void;
  existing?: boolean;
}) {
  const isNew = !existing;
  const keepCurrent = existing ? "Leave blank to keep current" : undefined;
  const [method, setMethod] = useState<Method | null>(
    existing ? "manual" : null
  );
  const [source, setSource] = useState<MetaSource>("url");
  const [xml, setXml] = useState("");
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [certError, setCertError] = useState<string | null>(null);
  const [certDragging, setCertDragging] = useState(false);

  function chooseMethod(next: Method | null) {
    setMethod(next);
    if (next !== "metadata") {
      setXml("");
      setPasteError(null);
      onConfigChange((f) => ({ ...f, metadataUrl: "", metadataXml: "" }));
    }
  }

  function chooseSource(next: MetaSource) {
    setSource(next);
    setPasteError(null);
    if (next === "url") {
      setXml("");
      onConfigChange((f) => ({ ...f, metadataXml: "" }));
    } else {
      onConfigChange((f) => ({ ...f, metadataUrl: "" }));
    }
  }

  // The raw XML is what we submit; samlify parses and validates it server-side
  // on save, so we don’t re-parse it here.
  function ingestXml(value: string) {
    setXml(value);
    setPasteError(null);
    onConfigChange((f) => ({ ...f, metadataXml: value.trim() }));
  }

  async function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    const isXml =
      file.type.includes("xml") || file.name.toLowerCase().endsWith(".xml");
    if (!isXml) {
      setPasteError("Only XML files are supported.");
      return;
    }
    ingestXml(await file.text());
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    void handleFiles(e.dataTransfer.files);
  }

  async function handleCertFile(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    const text = await file.text();
    if (!looksLikePemCert(text)) {
      setCertError(
        "That file doesn’t look like a Base64 certificate — download your IdP’s Certificate (Base64) variant."
      );
      return;
    }
    setCertError(null);
    onConfigChange((f) => ({ ...f, cert: text.trim() }));
  }

  function onCertDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setCertDragging(false);
    void handleCertFile(e.dataTransfer.files);
  }

  return (
    <>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md">
          How would you like to configure SAML?
        </Text>
      </Box>

      <Disclosure
        title="Exchange metadata"
        description="Import a metadata URL or paste XML — we read the rest"
        open={method === "metadata"}
        onOpenChange={(o) => chooseMethod(o ? "metadata" : null)}
      >
        <SubLabel>From your identity provider</SubLabel>
        <Box horizontal={8}>
          <ModeTabs
            options={META_SOURCES}
            value={source}
            onChange={chooseSource}
          />
        </Box>

        {source === "url" ? (
          <ManualField
            label="IdP metadata URL"
            hint="We’ll fetch and read your IdP’s metadata when you save the provider"
          >
            <Input
              value={config.metadataUrl}
              onChange={(e) =>
                onConfigChange((f) => ({ ...f, metadataUrl: e.target.value }))
              }
              placeholder="https://idp.example.com/saml/metadata"
              required={isNew}
              style={{ width: "100%" }}
            />
          </ManualField>
        ) : (
          <Field>
            <DropZone
              data-dragging={dragging}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <MonoTextarea
                value={xml}
                onChange={(e) => ingestXml(e.target.value)}
                placeholder="Paste your IdP’s SAML metadata XML, or drop a .xml file here"
                required={isNew}
                style={{ minHeight: 96 }}
              />
            </DropZone>
            {pasteError && (
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-error)">
                  {pasteError}
                </Text>
              </Box>
            )}
          </Field>
        )}
      </Disclosure>

      <Disclosure
        title="Enter manually"
        description="Type the IdP’s entity ID, sign-on URL, and certificate"
        open={method === "manual"}
        onOpenChange={(o) => chooseMethod(o ? "manual" : null)}
      >
        <SubLabel>From your identity provider</SubLabel>
        <ManualField
          label="IdP entity ID"
          hint="Verifies SAML responses come from the expected identity provider"
        >
          <Input
            value={config.issuer}
            onChange={(e) =>
              onConfigChange((f) => ({ ...f, issuer: e.target.value }))
            }
            placeholder={isNew ? "https://idp.example.com/saml" : keepCurrent}
            required={isNew}
            style={{ width: "100%" }}
          />
        </ManualField>

        <ManualField
          label="Sign-on URL"
          hint="Your IdP’s single sign-on endpoint"
        >
          <Input
            value={config.entryPoint}
            onChange={(e) =>
              onConfigChange((f) => ({ ...f, entryPoint: e.target.value }))
            }
            placeholder={
              isNew ? "https://your-org.okta.com/app/sso/saml" : keepCurrent
            }
            required={isNew}
            style={{ width: "100%" }}
          />
        </ManualField>

        <ManualField
          label="IdP certificate"
          hint="Used to verify SAML responses"
          error={certError}
        >
          <DropZone
            data-dragging={certDragging}
            onDragOver={(e) => {
              e.preventDefault();
              setCertDragging(true);
            }}
            onDragLeave={() => setCertDragging(false)}
            onDrop={onCertDrop}
          >
            <CertTextarea
              value={config.cert}
              onChange={(e) => {
                setCertError(null);
                onConfigChange((f) => ({ ...f, cert: e.target.value }));
              }}
              placeholder={
                isNew
                  ? "Paste the IdP’s X.509 certificate (PEM format), or drop a .cer file here"
                  : keepCurrent
              }
              required={isNew}
            />
          </DropZone>
        </ManualField>
      </Disclosure>
    </>
  );
}
